/**
 * registration-panel-content.ts
 * Builds strongly-typed panel data from i18n keys for each program variant.
 * Split from RegistrationProgramPanel.tsx to keep file size under 200 lines.
 */

import type { PricingRow } from './RegistrationPricingTable';

export type PanelVariant = 'van-developer' | 'simplify-developer' | 'van-demo' | 'simplify-demo';

export interface PanelData {
  badge: string;
  badgeStyle: 'van' | 'simplify';
  title: string;
  descriptions: string[];
  /** Capability bullets shown after descriptions (developer panels only) */
  capabilities?: string[];
  /** Introductory paragraphs before the tier name (developer panels only) */
  tierIntros?: string[];
  tierName: string;
  pricingRows: PricingRow[];
  includedHeader: string;
  included: string[];
  limitationsHeader?: string;
  limitations?: string[];
  ctaDescription: string;
  ctaLabel: string;
  /** External href for Simplify CTA; undefined = VAN (scroll-to-form button) */
  ctaHref?: string;
  notReadyTitle: string;
  notReadyDescription: string;
  notReadyHref: string;
  notReadyLinkText: string;
}

type TFunc = (key: string, fallback?: string) => string;

export function buildPanelData(variant: PanelVariant, t: TFunc): PanelData {
  switch (variant) {
    case 'van-developer':
      return {
        badge: t('DeveloperRegistration_VAN_Badge', 'ECGRID VAN'),
        badgeStyle: 'van',
        title: t('DeveloperRegistration_VAN_Title', 'ECGrid VAN Developer Program for B2B Connectivity'),
        descriptions: [
          t('DeveloperRegistration_VAN_Desc1'),
          t('DeveloperRegistration_VAN_Desc2'),
        ],
        capabilities: [
          t('DeveloperRegistration_VAN_Cap1'),
          t('DeveloperRegistration_VAN_Cap2'),
          t('DeveloperRegistration_VAN_Cap3'),
          t('DeveloperRegistration_VAN_Cap4'),
          t('DeveloperRegistration_VAN_Cap5'),
          t('DeveloperRegistration_VAN_Cap6'),
        ],
        tierIntros: [
          t('DeveloperRegistration_VAN_TierIntro1'),
          t('DeveloperRegistration_VAN_TierIntro2'),
        ],
        tierName: t('DeveloperRegistration_VAN_TierName', 'Tier: Developer'),
        pricingRows: [
          { label: t('DeveloperRegistration_VAN_PriceLabel_Monthly', 'Monthly price'), value: t('DeveloperRegistration_VAN_PriceValue_Monthly', '$100/month') },
          { label: t('DeveloperRegistration_VAN_PriceLabel_KCs', 'Included KCs'), value: t('DeveloperRegistration_VAN_PriceValue_KCs', '1,500 KCs') },
          { label: t('DeveloperRegistration_VAN_PriceLabel_Overage', 'KC overage rate'), value: t('DeveloperRegistration_VAN_PriceValue_Overage', '$0.10/KC') },
          { label: t('DeveloperRegistration_VAN_PriceLabel_Mailbox', 'Mailbox limit'), value: t('DeveloperRegistration_VAN_PriceValue_Mailbox', 'Up to 3 mailboxes') },
          { label: t('DeveloperRegistration_VAN_PriceLabel_Trial', 'Free trial'), value: t('DeveloperRegistration_VAN_PriceValue_Trial', 'First 30 days free -- no credit card required') },
        ],
        includedHeader: t('DeveloperRegistration_VAN_IncludedHeader', "WHAT'S INCLUDED"),
        included: [
          t('DeveloperRegistration_VAN_Included1'),
          t('DeveloperRegistration_VAN_Included2'),
          t('DeveloperRegistration_VAN_Included3'),
          t('DeveloperRegistration_VAN_Included4'),
          t('DeveloperRegistration_VAN_Included5'),
          t('DeveloperRegistration_VAN_Included6'),
          t('DeveloperRegistration_VAN_Included7'),
          t('DeveloperRegistration_VAN_Included8'),
          t('DeveloperRegistration_VAN_Included9'),
        ],
        ctaDescription: t('DeveloperRegistration_VAN_CtaDesc'),
        ctaLabel: t('DeveloperRegistration_VAN_CTA', 'Register for ECGrid VAN Developer'),
        notReadyTitle: t('DeveloperRegistration_VAN_NotReady_Title', 'Not ready for the full program?'),
        notReadyDescription: t('DeveloperRegistration_VAN_NotReady_Desc'),
        notReadyHref: 'https://demo.ecgrid.com',
        notReadyLinkText: t('DeveloperRegistration_VAN_NotReady_Link', 'Explore the VAN Demo'),
      };

    case 'simplify-developer':
      return {
        badge: t('DeveloperRegistration_Simplify_Badge', 'ECGRID SIMPLIFY'),
        badgeStyle: 'simplify',
        title: t('DeveloperRegistration_Simplify_Title', 'ECGrid Simplify Developer Program for Transformation Services'),
        descriptions: [
          t('DeveloperRegistration_Simplify_Desc1'),
          t('DeveloperRegistration_Simplify_Desc2'),
        ],
        capabilities: [
          t('DeveloperRegistration_Simplify_Cap1'),
          t('DeveloperRegistration_Simplify_Cap2'),
          t('DeveloperRegistration_Simplify_Cap3'),
          t('DeveloperRegistration_Simplify_Cap4'),
          t('DeveloperRegistration_Simplify_Cap5'),
          t('DeveloperRegistration_Simplify_Cap6'),
        ],
        tierIntros: [
          t('DeveloperRegistration_Simplify_TierIntro1'),
          t('DeveloperRegistration_Simplify_TierIntro2'),
        ],
        tierName: t('DeveloperRegistration_Simplify_TierName', 'Tier: Developer'),
        pricingRows: [
          { label: t('DeveloperRegistration_Simplify_PriceLabel_Monthly', 'Monthly price'), value: t('DeveloperRegistration_Simplify_PriceValue_Monthly', '$175/month') },
          { label: t('DeveloperRegistration_Simplify_PriceLabel_Docs', 'Included documents'), value: t('DeveloperRegistration_Simplify_PriceValue_Docs', '500 documents') },
          { label: t('DeveloperRegistration_Simplify_PriceLabel_Overage', 'Document overage rate'), value: t('DeveloperRegistration_Simplify_PriceValue_Overage', '$0.12/document') },
          { label: t('DeveloperRegistration_Simplify_PriceLabel_Setup', 'Setup fee'), value: t('DeveloperRegistration_Simplify_PriceValue_Setup', 'None') },
          { label: t('DeveloperRegistration_Simplify_PriceLabel_Connectors', 'Connectors'), value: t('DeveloperRegistration_Simplify_PriceValue_Connectors', 'Dev level') },
          { label: t('DeveloperRegistration_Simplify_PriceLabel_Trial', 'Free trial'), value: t('DeveloperRegistration_Simplify_PriceValue_Trial', 'First 30 days free -- no credit card required') },
        ],
        includedHeader: t('DeveloperRegistration_Simplify_IncludedHeader', "WHAT'S INCLUDED"),
        included: [
          t('DeveloperRegistration_Simplify_Included1'),
          t('DeveloperRegistration_Simplify_Included2'),
          t('DeveloperRegistration_Simplify_Included3'),
          t('DeveloperRegistration_Simplify_Included4'),
          t('DeveloperRegistration_Simplify_Included5'),
          t('DeveloperRegistration_Simplify_Included6'),
          t('DeveloperRegistration_Simplify_Included7'),
          t('DeveloperRegistration_Simplify_Included8'),
          t('DeveloperRegistration_Simplify_Included9'),
          t('DeveloperRegistration_Simplify_Included10'),
        ],
        ctaDescription: t('DeveloperRegistration_Simplify_CtaDesc'),
        ctaLabel: t('DeveloperRegistration_Simplify_CTA', 'Register for ECGrid Simplify Developer'),
        ctaHref: 'https://simplify.ecgrid.com/register',
        notReadyTitle: t('DeveloperRegistration_Simplify_NotReady_Title', 'Not ready for the full program?'),
        notReadyDescription: t('DeveloperRegistration_Simplify_NotReady_Desc'),
        notReadyHref: 'https://simplify.ecgrid.com/register?type=demo',
        notReadyLinkText: t('DeveloperRegistration_Simplify_NotReady_Link', 'Explore the Simplify Demo'),
      };

    case 'van-demo':
      return {
        badge: t('DemoRegistration_VAN_Badge', 'ECGRID VAN'),
        badgeStyle: 'van',
        title: t('DemoRegistration_VAN_Title', 'ECGrid VAN Demo'),
        descriptions: [
          t('DemoRegistration_VAN_Desc1'),
          t('DemoRegistration_VAN_Desc2'),
          t('DemoRegistration_VAN_Desc3'),
        ],
        tierName: t('DemoRegistration_VAN_TierName', 'Tier: Demo'),
        pricingRows: [
          { label: t('DemoRegistration_VAN_PriceLabel_Monthly', 'Monthly price'), value: t('DemoRegistration_VAN_PriceValue_Monthly', 'Free') },
          { label: t('DemoRegistration_VAN_PriceLabel_KCs', 'Included KCs'), value: t('DemoRegistration_VAN_PriceValue_KCs', '5,000 KCs') },
          { label: t('DemoRegistration_VAN_PriceLabel_Mailbox', 'Mailbox limit'), value: t('DemoRegistration_VAN_PriceValue_Mailbox', 'Up to 3 mailboxes (Reseller Network Node only)') },
          { label: t('DemoRegistration_VAN_PriceLabel_External', 'External connectivity'), value: t('DemoRegistration_VAN_PriceValue_External', 'Not available') },
          { label: t('DemoRegistration_VAN_PriceLabel_Cc', 'Credit card required'), value: t('DemoRegistration_VAN_PriceValue_Cc', 'No') },
        ],
        includedHeader: t('DemoRegistration_VAN_IncludedHeader', "WHAT'S INCLUDED"),
        included: [
          t('DemoRegistration_VAN_Included1'),
          t('DemoRegistration_VAN_Included2'),
          t('DemoRegistration_VAN_Included3'),
          t('DemoRegistration_VAN_Included4'),
          t('DemoRegistration_VAN_Included5'),
          t('DemoRegistration_VAN_Included6'),
          t('DemoRegistration_VAN_Included7'),
        ],
        limitationsHeader: t('DemoRegistration_VAN_LimitationsHeader', 'LIMITATIONS'),
        limitations: [
          t('DemoRegistration_VAN_Limitation1'),
          t('DemoRegistration_VAN_Limitation2'),
          t('DemoRegistration_VAN_Limitation3'),
        ],
        ctaDescription: t('DemoRegistration_VAN_CtaDesc'),
        ctaLabel: t('DemoRegistration_VAN_CTA', 'Register for ECGrid VAN Demo'),
        notReadyTitle: t('DemoRegistration_VAN_ReadyToBuild_Title', 'Ready to build?'),
        notReadyDescription: t('DemoRegistration_VAN_ReadyToBuild_Desc'),
        notReadyHref: 'https://developer.ecgrid.com',
        notReadyLinkText: t('DemoRegistration_VAN_ReadyToBuild_Link', 'Explore the Developer Program'),
      };

    case 'simplify-demo':
      return {
        badge: t('DemoRegistration_Simplify_Badge', 'ECGRID SIMPLIFY'),
        badgeStyle: 'simplify',
        title: t('DemoRegistration_Simplify_Title', 'ECGrid Simplify Demo'),
        descriptions: [
          t('DemoRegistration_Simplify_Desc1'),
          t('DemoRegistration_Simplify_Desc2'),
          t('DemoRegistration_Simplify_Desc3'),
        ],
        tierName: t('DemoRegistration_Simplify_TierName', 'Tier: Demo'),
        pricingRows: [
          { label: t('DemoRegistration_Simplify_PriceLabel_Monthly', 'Monthly price'), value: t('DemoRegistration_Simplify_PriceValue_Monthly', 'Free') },
          { label: t('DemoRegistration_Simplify_PriceLabel_Docs', 'Included documents'), value: t('DemoRegistration_Simplify_PriceValue_Docs', '100 documents') },
          { label: t('DemoRegistration_Simplify_PriceLabel_Mailbox', 'Mailbox limit'), value: t('DemoRegistration_Simplify_PriceValue_Mailbox', 'Up to 3 mailboxes') },
          { label: t('DemoRegistration_Simplify_PriceLabel_Connectors', 'Connectors'), value: t('DemoRegistration_Simplify_PriceValue_Connectors', 'UI upload/download only') },
          { label: t('DemoRegistration_Simplify_PriceLabel_Cc', 'Credit card required'), value: t('DemoRegistration_Simplify_PriceValue_Cc', 'No') },
        ],
        includedHeader: t('DemoRegistration_Simplify_IncludedHeader', "WHAT'S INCLUDED"),
        included: [
          t('DemoRegistration_Simplify_Included1'),
          t('DemoRegistration_Simplify_Included2'),
          t('DemoRegistration_Simplify_Included3'),
          t('DemoRegistration_Simplify_Included4'),
          t('DemoRegistration_Simplify_Included5'),
          t('DemoRegistration_Simplify_Included6'),
          t('DemoRegistration_Simplify_Included7'),
          t('DemoRegistration_Simplify_Included8'),
        ],
        limitationsHeader: t('DemoRegistration_Simplify_LimitationsHeader', 'LIMITATIONS'),
        limitations: [
          t('DemoRegistration_Simplify_Limitation1'),
          t('DemoRegistration_Simplify_Limitation2'),
          t('DemoRegistration_Simplify_Limitation3'),
        ],
        ctaDescription: t('DemoRegistration_Simplify_CtaDesc'),
        ctaLabel: t('DemoRegistration_Simplify_CTA', 'Register for ECGrid Simplify Demo'),
        ctaHref: 'https://simplify.ecgrid.com/register?type=demo',
        notReadyTitle: t('DemoRegistration_Simplify_ReadyToBuild_Title', 'Ready to build?'),
        notReadyDescription: t('DemoRegistration_Simplify_ReadyToBuild_Desc'),
        notReadyHref: 'https://simplify.ecgrid.com/register',
        notReadyLinkText: t('DemoRegistration_Simplify_ReadyToBuild_Link', 'Explore the Simplify Developer Program'),
      };
  }
}
